import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Timeout settings
const INACTIVITY_TIMEOUT_MS = 28 * 60 * 1000; // 28 minutes before showing warning
const COUNTDOWN_SECONDS = 120; // 2 minutes countdown (Total = 30 minutes)

export const AutoLogout: React.FC = () => {
  const [openWarning, setOpenWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const performLogout = useCallback(() => {
    localStorage.clear();
    setOpenWarning(false);
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?reason=inactivity';
    }
  }, []);

  const resetTimer = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setOpenWarning(false);
    setSecondsLeft(COUNTDOWN_SECONDS);

    // Set inactivity warning timer
    timerRef.current = setTimeout(() => {
      setOpenWarning(true);
      setSecondsLeft(COUNTDOWN_SECONDS);

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            performLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT_MS);
  }, [performLogout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleUserActivity = () => {
      // Only reset timer if warning dialog is not open
      if (!openWarning) {
        resetTimer();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      if (timerRef.current) clearTimeout(timerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [resetTimer, openWarning]);

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  return (
    <Dialog 
      open={openWarning} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          setOpenWarning(false);
        }
      }}
      maxWidth="xs" 
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main', fontWeight: 'bold' }}>
        <WarningAmberIcon fontSize="large" /> แจ้งเตือนการหมดเวลาใช้งาน
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          เนื่องจากไม่มีการใช้งานระบบเป็นเวลานาน เพื่อความปลอดภัยของข้อมูล ระบบจะออกจากระบบโดยอัตโนมัติใน:
        </Typography>
        <Box sx={{ textAlign: 'center', py: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
            {Math.floor(secondsLeft / 60)}:{(secondsLeft % 60).toString().padStart(2, '0')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            นาที : วินาที
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
          กดปุ่ม <b>"ใช้งานต่อ"</b> เพื่อรักษาสถานะการเข้าสู่ระบบ
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={performLogout} color="error">
          ออกจากระบบทันที
        </Button>
        <Button onClick={handleStayLoggedIn} variant="contained" color="primary" sx={{ px: 3, fontWeight: 'bold' }}>
          ใช้งานต่อ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoLogout;
